<template>
  <div class="jbu-page">
    <div class="container jbu-page__container">
      <HcBreadcrumb>
        <li class="breadcrumb-item"><a href="/">Home</a></li>
        <li class="breadcrumb-item"><a href="/jobs">Jobs</a></li>
        <li class="breadcrumb-item active" aria-current="page">{{ pageTitle }}</li>
      </HcBreadcrumb>

      <header class="jbu-header hp-animate">
        <h1 class="jbu-header__title">{{ pageTitle }}</h1>
        <p class="jbu-header__subtitle">{{ pageSubtitle }}</p>
        <span v-if="!loading && total" class="jbu-header__count">{{ total }} browse links</span>
      </header>

      <div v-if="loading" class="jbu-skeleton-grid" aria-busy="true" aria-label="Loading">
        <div class="jbu-skeleton-card" v-for="n in 9" :key="n">
          <content-loader viewBox="0 0 280 20" primaryColor="#f1f5f9" secondaryColor="#e2e8f0">
            <rect x="0" y="4" rx="4" ry="4" width="200" height="12" />
          </content-loader>
        </div>
      </div>

      <template v-else>
        <section
          v-if="showMetroSection"
          class="jbu-section hp-animate"
        >
          <h2 class="jbu-section__title">Jobs in metro cities</h2>
          <div class="jbu-metro-scroll">
            <a
              v-for="(job, index) in popular_cities_jobs"
              :key="'metro-' + index"
              :href="job.search_url"
              class="jbu-metro-tile"
            >
              <div class="jbu-metro-tile__icon">
                <img
                  v-if="cityImage(job.name)"
                  :src="$S3_url + cityImage(job.name)"
                  :alt="'Jobs in ' + job.name"
                  loading="lazy"
                />
                <span v-else class="jbu-metro-tile__icon--letter">{{ cityInitial(job.name) }}</span>
              </div>
              <span class="jbu-metro-tile__name">{{ job.name }}</span>
            </a>
          </div>
        </section>

        <section v-if="jobs.length" class="jbu-section hp-animate">
          <h2 class="jbu-section__title">
            {{ allSectionTitle }}
            <span v-if="showMetroSection"> · {{ jobs.length }} more</span>
          </h2>
          <div class="jbu-grid">
            <a
              v-for="(job, index) in jobs"
              :key="index"
              :href="job.search_url"
              class="jbu-link-card"
            >
              <p class="jbu-link-card__text">
                {{ jobLabel(job) }}
                <span
                  v-if="job.static_id == 6 && job.course"
                  class="jbu-link-card__sub"
                >{{ job.course.course_name }}</span>
              </p>
              <span class="jbu-link-card__arrow" aria-hidden="true">→</span>
            </a>
          </div>
        </section>

        <div v-else-if="!showMetroSection" class="jbu-empty hp-animate">
          <p class="jbu-empty__title">No browse links found</p>
          <p class="jbu-empty__text">
            We could not load links for this category right now. Try exploring jobs directly or
            <span v-if="!isLogin"><a href="/jobseeker/register">create a free account</a> for job alerts.</span>
            <span v-else>check back soon for updates.</span>
          </p>
          <a href="/jobs" class="hp-btn hp-btn--primary">Browse all jobs</a>
        </div>

        <nav class="jbu-quick-nav hp-animate" aria-label="Browse jobs by">
          <p class="jbu-quick-nav__label">Browse jobs by</p>
          <a
            v-for="item in quickNavItems"
            :key="item.path"
            :href="item.path"
            class="jbu-quick-nav__link"
            :class="{ 'jbu-quick-nav__link--active': item.active }"
          >{{ item.label }}</a>
        </nav>
      </template>
    </div>
  </div>
</template>

<script>
import Actions from '../libraries/apiActions.js';
import { mapState, mapGetters } from 'vuex';
import store from '../store';
import { STATES } from '../store/modules/states/constants';
import { ContentLoader } from 'vue-content-loader';
import HcBreadcrumb from '@/components/chrome/HcBreadcrumb.vue';
import { POPULAR_CITIES } from '@/constants/homePageData';

import '@/assets/home-page.css';
import '@/assets/jobs-by-url-page.css';

const PAGE_TYPES = [
  { value: 1, text: 'Jobs by cities', url: 'Jobs-by-cities', path: '/jobs-by-cities', subtitle: 'Browse fresher and entry-level openings by city across India.' },
  { value: 2, text: 'Jobs by courses', url: 'Jobs-by-courses', path: '/jobs-by-courses', subtitle: 'Find openings that match your qualification and course.' },
  { value: 3, text: 'Jobs by roles', url: 'Jobs-by-roles', path: '/jobs-by-roles', subtitle: 'Explore vacancies by job role and designation.' },
  { value: 4, text: 'Jobs by category', url: 'Jobs-by-categories', path: '/jobs-by-categories', subtitle: 'Browse jobs grouped by industry and category.' },
  { value: 5, text: 'Jobs by company', url: 'Jobs-by-company', path: '/jobs-by-company', subtitle: 'See active openings from companies hiring on Hireclap.' },
  { value: 6, text: 'Jobs by branch', url: 'Jobs-by-branches', path: '/jobs-by-branches', subtitle: 'Find jobs by engineering and degree branch.' },
  { value: 7, text: 'Jobs by courses in location', url: 'Jobs-by-courses-in-location', path: '/jobs-by-courses', subtitle: 'Course-wise jobs filtered by location.' },
  { value: 8, text: 'Jobs by role in location', url: 'Jobs-by-role-in-location', path: '/jobs-by-roles', subtitle: 'Role-wise jobs filtered by location.' },
  { value: 9, text: 'Jobs by category in location', url: 'Jobs-by-category-in-location', path: '/jobs-by-categories', subtitle: 'Category-wise jobs filtered by location.' },
  { value: 10, text: 'Jobs by company in location', url: 'Jobs-by-company-in-location', path: '/jobs-by-company', subtitle: 'Company-wise jobs filtered by location.' },
  { value: 11, text: 'Jobs by branch in location', url: 'Jobs-by-branch-in-location', path: '/jobs-by-branches', subtitle: 'Branch-wise jobs filtered by location.' },
  { value: 12, text: 'Fresher jobs by cities', url: 'Fresher-jobs-in-cities', path: '/fresher-jobs-in-cities', subtitle: 'Entry-level and fresher openings organised by city.' },
];

const QUICK_NAV = [
  { label: 'Cities', path: '/jobs-by-cities', route: 'Jobs-by-cities' },
  { label: 'Courses', path: '/jobs-by-courses', route: 'Jobs-by-courses' },
  { label: 'Roles', path: '/jobs-by-roles', route: 'Jobs-by-roles' },
  { label: 'Categories', path: '/jobs-by-categories', route: 'Jobs-by-categories' },
  { label: 'Companies', path: '/jobs-by-company', route: 'Jobs-by-company' },
  { label: 'Branches', path: '/jobs-by-branches', route: 'Jobs-by-branches' },
  { label: 'Fresher cities', path: '/fresher-jobs-in-cities', route: 'Fresher-jobs-in-cities' },
];

const CITY_IMAGES = POPULAR_CITIES.reduce((acc, city) => {
  if (!city.isMore) acc[city.name.toLowerCase()] = city.image;
  return acc;
}, {});

const SECTION_TITLES = {
  1: 'Jobs in cities across India',
  2: 'Browse by course',
  3: 'Browse by role',
  4: 'Browse by category',
  5: 'Browse by company',
  6: 'Browse by branch',
  12: 'Fresher jobs across cities',
};

export default {
  name: 'JobsByUrl',
  components: {
    ContentLoader,
    HcBreadcrumb,
  },
  metaInfo() {
    return {
      title: `${this.pageTitle} | Hireclap`,
      script: [{ type: 'application/ld+json', json: this.breadcrumbJson }],
    };
  },
  data() {
    return {
      action: new Actions(),
      type: 0,
      total: 0,
      jobs: [],
      loading: true,
      popular_cities_jobs: [],
      popular_cities: [],
      types: PAGE_TYPES,
      isLogin: false,
    };
  },
  beforeCreate() {
    this.$store = store;
  },
  beforeMount() {
    this.isLogin = !!(this.$cookies.get('hire-seeker') && this.$cookies.get('hire-seeker').token);
  },
  created() {
    const apiFn = this.action;
    this.cachedDispatch(STATES.GET_STATES, { param: apiFn });
    const match = this.types.find(t => t.url === this.$route.name);
    if (match) this.type = match.value;

    this.action.getUrls(this.type, this.offset).then((res) => {
      this.total = res.total;
      const sorted = res.url.sort((a, b) => (a.name > b.name) - (a.name < b.name));

      if (this.type === 1) {
        this.locations.forEach((location) => {
          if (location.group === 'Top Metropolitan Cities') {
            this.popular_cities.push(location.id);
          }
        });
        this.popular_cities_jobs = sorted.filter(job => this.popular_cities.includes(job.city_id));
        this.jobs = sorted.filter(job => !this.popular_cities.includes(job.city_id));
      } else {
        this.jobs = sorted;
      }

      this.loading = false;
    });
  },
  computed: {
    ...mapGetters(['cachedDispatch']),
    ...mapState({
      locations: state => state.states.locations.locations,
    }),
    pageTitle() {
      const match = this.types.find(t => t.value === this.type);
      return match ? match.text : this.$route.name.replace(/-/g, ' ');
    },
    pageSubtitle() {
      const match = this.types.find(t => t.value === this.type);
      return match ? match.subtitle : 'Browse job openings on Hireclap.';
    },
    showMetroSection() {
      return this.type === 1 && this.popular_cities_jobs.length > 0;
    },
    allSectionTitle() {
      return SECTION_TITLES[this.type] || 'All browse links';
    },
    quickNavItems() {
      return QUICK_NAV.map(item => ({
        ...item,
        active: item.route === this.$route.name,
      }));
    },
    breadcrumbJson() {
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [{
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: window.location.origin,
        }, {
          '@type': 'ListItem',
          position: 2,
          name: 'Jobs',
          item: `${window.location.origin}/jobs`,
        }, {
          '@type': 'ListItem',
          position: 3,
          name: this.pageTitle,
          item: window.location.href,
        }],
      };
    },
  },
  methods: {
    jobLabel(job) {
      if (job.static_id === 6 && job.course) {
        return job.name;
      }
      return job.name;
    },
    cityImage(name) {
      if (!name) return '';
      const key = name.toLowerCase().replace(/\s+/g, '');
      const normalized = name.toLowerCase();
      return CITY_IMAGES[normalized] || CITY_IMAGES[key] || '';
    },
    cityInitial(name) {
      return name ? name.charAt(0).toUpperCase() : '?';
    },
  },
};
</script>
